/**
 * Phase 3 — OffloadModal
 *
 * Shown when the user clicks "Download to my device" on a message.
 * Two paths:
 *  A) Encrypted offload → Download via SAS → store in IndexedDB → confirm delete on server.
 *     To read later: message read via /api/offload/decrypt-local (platform mediates decrypt).
 *  B) Unencrypted export → Server decrypts → downloads as .html file. Server copy kept.
 *     Permanent readable archive — no future fees.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, CircularProgress,
  Alert, LinearProgress, Divider, Chip,
} from '@mui/material';
import DownloadIcon        from '@mui/icons-material/Download';
import DevicesIcon         from '@mui/icons-material/Devices';
import ArticleIcon         from '@mui/icons-material/Article';
import CheckCircleIcon     from '@mui/icons-material/CheckCircle';
import WarningAmberIcon    from '@mui/icons-material/WarningAmber';
import api                 from '../../services/api';
import {
  downloadAndStore,
  getTotalUsageBytes,
} from '../../services/offloadedMessageStore';

export interface OffloadModalProps {
  open:     boolean;
  mailId:   string;
  subject:  string;
  fromName: string;
  sentDate: string;
  sizeBytes?: number | null;
  onClose:   () => void;
  onOffloadComplete?: (mailId: string) => void;
}

type Step = 'choose' | 'downloading' | 'confirming' | 'done' | 'error';

function fmtSize(b?: number | null) {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1_048_576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1_048_576).toFixed(2)} MB`;
}
function fmtStorage(b: number) {
  if (b < 1024) return `${b} B`;
  if (b < 1_048_576) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1_073_741_824) return `${(b / 1_048_576).toFixed(1)} MB`;
  return `${(b / 1_073_741_824).toFixed(2)} GB`;
}

const OffloadModal: React.FC<OffloadModalProps> = ({
  open, mailId, subject, fromName, sentDate, sizeBytes, onClose, onOffloadComplete,
}) => {
  const [step, setStep]               = useState<Step>('choose');
  const [error, setError]             = useState('');
  const [idbUsage, setIdbUsage]       = useState(0);
  const [progress, setProgress]       = useState('');

  useEffect(() => {
    if (open) {
      setStep('choose');
      setError('');
      getTotalUsageBytes().then(setIdbUsage).catch(() => {});
    }
  }, [open]);

  // ── Path A: Encrypted offload to IndexedDB ────────────────────────────────
  const handleDeviceOffload = async () => {
    setStep('downloading');
    setProgress('Fetching download URL…');
    setError('');

    try {
      const infoResp = await api.get<{
        sasUrl: string; isEncrypted: boolean; sizeBytes: number; expiresAt: string;
      }>(`/api/offload/${mailId}/download-url`);

      const { sasUrl, sizeBytes: sz } = infoResp;
      if (!sasUrl) throw new Error('Could not generate download URL.');

      setProgress('Downloading message body…');
      const ok = await downloadAndStore(sasUrl, {
        mailId, subject, fromName, sentDate, sizeBytes: sz ?? sizeBytes ?? 0,
      });
      if (!ok) throw new Error('Download failed — please try again.');

      setProgress('Confirming and deleting server copy…');
      setStep('confirming');

      await api.post(`/api/offload/${mailId}/confirm`);
      setStep('done');
      onOffloadComplete?.(mailId);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Offload failed.');
      setStep('error');
    }
  };

  // ── Path B: Plaintext download (server decrypts, user saves .html) ────────
  const handlePlaintextDownload = async () => {
    setStep('downloading');
    setProgress('Decrypting and preparing download…');
    setError('');

    try {
      const baseUrl = api.getBaseUrl();
      const token = api.getAccessToken() ?? localStorage.getItem('accessToken') ?? '';
      const resp = await fetch(
        `${baseUrl}/api/offload/${mailId}/download-plaintext`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { message?: string };
        throw new Error(body.message ?? `Server error ${resp.status}`);
      }

      const disposition = resp.headers.get('Content-Disposition') ?? '';
      const nameMatch   = disposition.match(/filename="?([^";]+)"?/);
      const fileName    = nameMatch?.[1] ?? `message_${mailId.slice(0, 8)}.html`;

      const blob = await resp.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);

      setStep('done');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Download failed.');
      setStep('error');
    }
  };

  const size = fmtSize(sizeBytes);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Download Message to Your Device
      </DialogTitle>

      <DialogContent>
        {/* Message info */}
        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1 }}>
          <Typography variant="subtitle2" noWrap fontWeight={600}>{subject}</Typography>
          <Typography variant="caption" color="text.secondary">
            From: {fromName} · {sentDate?.substring(0, 8)}
            {size ? ` · ${size}` : ''}
          </Typography>
        </Box>

        {/* ── Choose step ────────────────────────────────────────────── */}
        {step === 'choose' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose how to save this message locally. Downloaded messages can be read
              without paying future retrieval fees.
            </Typography>

            {/* Option A */}
            <Box
              sx={{
                border: '1px solid #e0e0e0', borderRadius: 2, p: 2, mb: 2, cursor: 'pointer',
                '&:hover': { borderColor: '#1976d2', bgcolor: '#f5faff' },
              }}
              onClick={handleDeviceOffload}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <DevicesIcon color="primary" />
                <Typography fontWeight={600}>Save to This Device (Encrypted)</Typography>
                <Chip label="Recommended" size="small" color="primary" variant="outlined" />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Downloads the encrypted blob directly from Azure and stores it in your
                browser's local storage (IndexedDB). The server copy is then deleted —
                saving storage costs. To read it, you still sign in here; we decrypt on-demand.
              </Typography>
              <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                ⚠️ Message will only be available on this browser/device.
              </Typography>
            </Box>

            {/* Option B */}
            <Box
              sx={{
                border: '1px solid #e0e0e0', borderRadius: 2, p: 2, cursor: 'pointer',
                '&:hover': { borderColor: '#388e3c', bgcolor: '#f1f9f1' },
              }}
              onClick={handlePlaintextDownload}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <ArticleIcon sx={{ color: '#388e3c' }} />
                <Typography fontWeight={600}>Export Readable Copy (.html)</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Decrypts the message on our server and downloads it as a self-contained
                HTML file you can open in any browser — forever, without needing your account.
                The server copy is kept unchanged.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Good for archiving. Does not delete the server copy or eliminate future fees.
              </Typography>
            </Box>

            {idbUsage > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                Local archive uses {fmtStorage(idbUsage)} of browser storage.
              </Typography>
            )}
          </>
        )}

        {/* ── Downloading / confirming step ─────────────────────────── */}
        {(step === 'downloading' || step === 'confirming') && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography>{progress}</Typography>
            {step === 'confirming' && (
              <LinearProgress sx={{ mt: 2 }} />
            )}
          </Box>
        )}

        {/* ── Done ──────────────────────────────────────────────────── */}
        {step === 'done' && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="h6" gutterBottom>Done!</Typography>
            <Typography variant="body2" color="text.secondary">
              Your message has been saved to your device. The server copy has been deleted.
              You can read it any time from your local archive.
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              To read it: click the message in your inbox (it will appear with a{' '}
              <strong>Local</strong> badge). We'll decrypt it on-demand when you open it.
            </Typography>
          </Box>
        )}

        {/* ── Error ─────────────────────────────────────────────────── */}
        {step === 'error' && (
          <Box>
            <Alert severity="error" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Your server copy has not been deleted. Please try again.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {(step === 'done' || step === 'error') && (
          <Button onClick={onClose} variant="contained" startIcon={<DownloadIcon />}>
            Close
          </Button>
        )}
        {step === 'choose' && (
          <Button onClick={onClose} color="inherit">Cancel</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OffloadModal;
